import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import { Button, Input, TextArea } from '../../ui';
import { BrowseTabPreview } from './BrowseTabPreview';
import {
  buildDetailRows,
  copyPrompt,
  readEffectiveNegativePrompt,
  readEffectivePrompt,
  readGenerationRecipe,
} from './MediaMetadataInspector';
import { mediaLabel } from './mediaCardUtils';
import MediaLineage, { RelatedMedia } from './MediaLineage';
import ConfirmModal from '../../modals/ConfirmModal';
import GenerationRecipeModal from './GenerationRecipeModal';
import CropImageModal from './CropImageModal';
import { constructMediaUrl } from '../../../api/helpers';

interface MediaInspectorProps {
  item: MediaLibraryItem;
  mode: 'manage' | 'select';
  /** Render as a bottom sheet (modal/narrow) rather than a right drawer (page). */
  asSheet?: boolean;

  /** Position within the current result set, for ◀ N / M ▶ filmstrip nav. */
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;

  // manage-mode editing (optional — omitted in select mode)
  contextDraft?: string;
  renameDraft?: string;
  savingContext?: boolean;
  renaming?: boolean;
  deleting?: boolean;
  onContextDraftChange?: (value: string) => void;
  onRenameDraftChange?: (value: string) => void;
  onSaveContext?: () => void;
  onRename?: () => void;
  onRemove?: () => void;

  // stored AI description (optional — omitted in select mode)
  aiDescription?: { text: string; model: string; userEdited: boolean };
  describing?: boolean;
  onDescribe?: (force: boolean) => void;
  descriptionDraft?: string;
  onDescriptionDraftChange?: (v: string) => void;
  onSaveDescription?: () => void;
  savingDescription?: boolean;

  // select-mode affordance
  isSelected?: boolean;
  onToggleSelect?: (mediaId: string) => void;

  onToggleFavourite?: (mediaId: string, current: boolean) => void;
  togglingFavourite?: boolean;

  /** Lineage (parent / siblings / variations) for the open item — optional. */
  related?: RelatedMedia;
  relatedLoading?: boolean;
  /** Click-to-navigate to a related item by media id. */
  onNavigateRelated?: (mediaId: string) => void;

  /**
   * Suppress manage-mode editing controls (context/rename/remove) for this item.
   * Used when viewing an off-page lineage item whose row isn't loaded into the
   * editable parent state — favourite, copy-prompt and lineage still work.
   */
  readOnly?: boolean;
  onCropSaved?: (item: MediaLibraryItem) => void | Promise<void>;

  /**
   * Pull the clip this media belongs to back into the session and open it.
   * Only offered for items that actually carry a `clip_id` — media generated by
   * a pipeline that never became a clip has nothing to restore.
   */
  onRestoreClip?: (clipId: string) => void;
  restoringClip?: boolean;
}

/**
 * The promoted, image-forward inspector — a dockable right drawer (page) or
 * bottom sheet (modal). The preview is the **hero**: it dominates the panel and
 * shows the full-resolution original. Everything else (prompt, the mono spec
 * table, lineage, and manage-mode editing) is **progressively disclosed** behind
 * a single "Details" toggle, so the default view is the image, not a spec sheet.
 *
 * Keeps copy-prompt, ◀▶ filmstrip nav through the current result set, keyboard
 * (← / → navigate, Esc closes), focus trap + restore to the originating card.
 */
const MediaInspector: React.FC<MediaInspectorProps> = ({
  item,
  mode,
  asSheet = false,
  index,
  total,
  onPrev,
  onNext,
  onClose,
  contextDraft = '',
  renameDraft = '',
  savingContext = false,
  renaming = false,
  deleting = false,
  onContextDraftChange,
  onRenameDraftChange,
  onSaveContext,
  onRename,
  onRemove,
  aiDescription,
  describing = false,
  onDescribe,
  descriptionDraft = '',
  onDescriptionDraftChange,
  onSaveDescription,
  savingDescription = false,
  isSelected = false,
  onToggleSelect,
  onToggleFavourite,
  togglingFavourite = false,
  related,
  relatedLoading = false,
  onNavigateRelated,
  readOnly = false,
  onCropSaved,
  onRestoreClip,
  restoringClip = false,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const mediaId = item.media_id || item.id;
  const rows = React.useMemo(() => buildDetailRows(item), [item]);
  const generationRecipe = React.useMemo(() => readGenerationRecipe(item), [item]);
  const effectivePrompt = React.useMemo(() => readEffectivePrompt(item), [item]);
  const effectiveNegativePrompt = React.useMemo(() => readEffectiveNegativePrompt(item), [item]);
  const promptWasAugmented = Boolean(effectivePrompt && effectivePrompt !== item.prompt);
  const label = mediaLabel(item);
  const originalUrl = item.url ? constructMediaUrl(item.url) : '';

  // Manage-mode editing is shown only for editable (on-page) items.
  const canEdit = mode === 'manage' && !readOnly;

  // Progressive disclosure — image-forward by default; details on demand.
  const [showDetails, setShowDetails] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [showRecipe, setShowRecipe] = React.useState(false);
  const [showCrop, setShowCrop] = React.useState(false);
  const hasLineage =
    !!related && (!!related.parent || related.variations.length > 0 || related.siblings.length > 0);
  const hasDetails = rows.length > 0 || !!item.prompt || canEdit || hasLineage;
  // Only media that is actually attached to a clip can restore one. Pipeline
  // media that never became a clip gets no affordance at all — not a disabled
  // one.
  const restorableClipId = (item.clip_id || '').trim();
  const deleteLabel = item.type === 'image' ? 'Delete image' : 'Delete file';
  const deleteDisabled = deleting || item.status === 'generating' || !onRemove;

  React.useEffect(() => {
    setShowDeleteConfirm(false);
    setShowRecipe(false);
    setShowCrop(false);
  }, [mediaId]);

  // Move focus into the panel on open; restore to the previously-focused element
  // (the originating card) on unmount.
  React.useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, []);

  // ←/→ filmstrip nav + Esc close, while the inspector is open.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onNext();
    }
  };

  return (
    <>
      <div className="media-inspector-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className={`media-inspector${asSheet ? ' as-sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Media inspector"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="media-inspector-header">
          <div className="media-inspector-nav">
            <button
              type="button"
              className="media-inspector-navbtn"
              onClick={onPrev}
              disabled={total <= 1}
              aria-label="Previous item"
            >
              ◀
            </button>
            <span>
              {index + 1} / {total}
            </span>
            <button
              type="button"
              className="media-inspector-navbtn"
              onClick={onNext}
              disabled={total <= 1}
              aria-label="Next item"
            >
              ▶
            </button>
          </div>
          <button
            type="button"
            className="media-inspector-navbtn"
            onClick={onClose}
            aria-label="Close inspector"
          >
            ✕
          </button>
        </div>

        {/* HERO — the full-resolution preview, the dominant element. */}
        <div className="media-inspector-hero">
          <BrowseTabPreview item={item} mode="hero" />
        </div>

        {/* Compact action row — always visible, image-forward. */}
        <div className="media-inspector-actions">
          <p className="media-inspector-label" title={label}>
            {label}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {onToggleFavourite && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onToggleFavourite(mediaId, !!item.is_favourite)}
                disabled={togglingFavourite}
              >
                {item.is_favourite ? '★ Favourited' : '☆ Favourite'}
              </Button>
            )}
            {originalUrl && item.status === 'ready' && (
              <a className="btn btn-ghost btn-sm" href={originalUrl} target="_blank" rel="noreferrer">
                Open original
              </a>
            )}
            {item.prompt && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copyPrompt(item.prompt || '')}
              >
                ⧉ Copy prompt
              </Button>
            )}
            {promptWasAugmented && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copyPrompt(effectivePrompt)}
              >
                ⧉ Copy effective prompt
              </Button>
            )}
            {generationRecipe && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowRecipe(true)}
              >
                View recipe
              </Button>
            )}
            {onRestoreClip && restorableClipId && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onRestoreClip(restorableClipId)}
                disabled={restoringClip}
                title="Reload the clip this file belongs to and open it"
              >
                {restoringClip ? 'Restoring...' : 'Restore clip'}
              </Button>
            )}
            {canEdit && item.type === 'image' && item.status === 'ready' && (
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowCrop(true)}>
                Crop copy
              </Button>
            )}
            {mode === 'select' && onToggleSelect && (
              <Button
                type="button"
                variant={isSelected ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => onToggleSelect(mediaId)}
              >
                {isSelected ? 'Deselect' : 'Select'}
              </Button>
            )}
            {canEdit && (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteDisabled}
                title={item.status === 'generating' ? 'Wait until generation finishes before deleting.' : undefined}
              >
                {deleting ? 'Deleting...' : deleteLabel}
              </Button>
            )}
            {hasDetails && (
              <button
                type="button"
                className="media-inspector-disclose"
                aria-expanded={showDetails}
                onClick={() => setShowDetails((v) => !v)}
              >
                {showDetails ? '▾ Details' : '▸ Details'}
              </button>
            )}
          </div>
        </div>

        {/* Progressively-disclosed details — hidden until toggled. */}
        {hasDetails && showDetails && (
          <div className="media-inspector-details">
            {/* full prompt */}
            {item.prompt && (
              <div>
                <p className="attachment-meta font-medium mb-1">Prompt</p>
                <p className="attachment-meta whitespace-pre-wrap break-words">{item.prompt}</p>
              </div>
            )}
            {promptWasAugmented && (
              <div>
                <p className="attachment-meta font-medium mb-1">Effective prompt</p>
                <p className="attachment-meta whitespace-pre-wrap break-words">{effectivePrompt}</p>
              </div>
            )}
            {effectiveNegativePrompt && (
              <div>
                <p className="attachment-meta font-medium mb-1">Negative prompt</p>
                <p className="attachment-meta whitespace-pre-wrap break-words">{effectiveNegativePrompt}</p>
              </div>
            )}

            {/* 2-col mono spec table */}
            {rows.length > 0 && (
              <div>
                <p className="attachment-meta font-medium mb-1">Details</p>
                <div className="media-spec-table">
                  {rows.map((row) => (
                    <React.Fragment key={row.label}>
                      <span className="media-spec-key">{row.label}</span>
                      <span className="media-spec-val">
                        {row.href ? (
                          <a href={row.href} className="underline decoration-dotted">
                            {row.value}
                          </a>
                        ) : (
                          row.value
                        )}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* lineage — parent / siblings / variations thumbnails */}
            <MediaLineage
              related={related}
              loading={relatedLoading}
              onNavigate={onNavigateRelated}
            />

            {/* manage-mode editing (suppressed for read-only off-page items) */}
            {canEdit && (
              <div className="space-y-2 pt-1 border-t border-white/10">
                <TextArea
                  value={contextDraft}
                  onChange={(e) => onContextDraftChange?.(e.target.value)}
                  rows={3}
                  placeholder="Add context for this file (stored in metadata)..."
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onSaveContext}
                  disabled={savingContext}
                >
                  {savingContext ? 'Saving...' : 'Save Context'}
                </Button>

                {/* Stored AI description — vision-model caption, generate or edit.
                    Images only: the describe route rejects clips/audio/documents
                    (describableType in the backend), so the affordance would just
                    error for anything else. */}
                {item.type === 'image' && (
                  <div className="space-y-2 pt-1" data-testid="media-ai-description">
                    <p className="attachment-meta font-medium mb-1">
                      AI description
                      {aiDescription?.text && (
                        <span className="attachment-meta font-normal">
                          {' '}
                          —{' '}
                          {aiDescription.userEdited ? 'edited by you' : `by ${aiDescription.model}`}
                        </span>
                      )}
                    </p>
                    {!aiDescription?.text && (
                      <p className="attachment-meta" data-testid="media-ai-description-notice">
                        No description yet — generators and the reviewer are told what this
                        image shows. Describe it or write one.
                      </p>
                    )}
                    <TextArea
                      value={descriptionDraft}
                      onChange={(e) => onDescriptionDraftChange?.(e.target.value)}
                      rows={3}
                      placeholder="No description yet — generate one with AI or write your own."
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid="media-ai-describe"
                        onClick={() => onDescribe?.(Boolean(aiDescription?.text))}
                        disabled={describing || savingDescription}
                      >
                        {describing
                          ? 'Describing...'
                          : aiDescription?.text
                            ? 'Re-describe'
                            : 'Describe with AI'}
                      </Button>
                      {descriptionDraft !== (aiDescription?.text || '') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid="media-ai-save"
                          onClick={onSaveDescription}
                          disabled={savingDescription || describing}
                        >
                          {savingDescription ? 'Saving...' : 'Save'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <Input
                  value={renameDraft}
                  onChange={(e) => onRenameDraftChange?.(e.target.value)}
                  placeholder="Rename file..."
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onRename}
                    disabled={renaming || !renameDraft.trim()}
                  >
                    {renaming ? 'Renaming...' : 'Rename'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => onRemove?.()}
        title={deleteLabel === 'Delete image' ? 'Delete this image?' : 'Delete this file?'}
        message={`Delete "${label}" permanently? This removes the stored original and its thumbnails. This cannot be undone.`}
        confirmText={deleteLabel}
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
      {generationRecipe && (
        <GenerationRecipeModal item={item} isOpen={showRecipe} onClose={() => setShowRecipe(false)} />
      )}
      <CropImageModal item={item} isOpen={showCrop} onClose={() => setShowCrop(false)} onSaved={onCropSaved} />
    </>
  );
};

export default MediaInspector;
